import React, { useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import style from './fileupload.module.scss';
import upload from '../../Assets/images/bootstrap-upload.png';
import { cvUpload, cvParse, cvOversize } from '../../actions/uploadData';
import { FilesAdded } from './FilesAddedComponent';
import { errorsMessages, CV_FILE_ALLOWED_FORMATS, MAX_CV_FILE_SIZE, validationMessages } from '../../utils/constants';

const maxFileUploadSize = MAX_CV_FILE_SIZE;
const correctFileExt = CV_FILE_ALLOWED_FORMATS;

export function DropOrChooseFile() {
  const [files, setFiles] = React.useState([]);
  const [filesNumber, setFilesNumber] = React.useState(0);
  const [wrongFileType, setWrongFileType] = React.useState(false);
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const validationMessage = useMemo(() => validationMessages(t), [t]);
  const errorMessage = useMemo(() => errorsMessages(t), [t]);
  const error = useSelector((state) =>
    state.uploadData.error === 'Network Error' ? errorMessage.NETWORK_ERROR : state.uploadData.error
  );
  const pendingParse = useSelector((state) => state.uploadData.pendingParse);
  const navigate = useNavigate();
  function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  }

  const prevProps = usePrevious({ pendingParse });

  useEffect(() => {
    if (prevProps?.pendingParse && !pendingParse && !error) {
      navigate('/dashboard/profile-builder-two');
    }
  }, [pendingParse, error]);

  const onDropHandler = (ev) => {
    ev.stopPropagation();
    ev.preventDefault();
    setWrongFileType(false);
    const filesAmount = Object.keys(ev.dataTransfer.items).length;
    setFilesNumber(filesAmount);
    dispatch(cvOversize(false));
    if (filesAmount > 1) return;
    if (!correctFileExt.includes(ev.dataTransfer.files[0].name.split('.').pop())) {
      setWrongFileType(true);
      return;
    }
    if (maxFileUploadSize < ev.dataTransfer.files[0].size) {
      dispatch(cvOversize(true));
      return;
    }
    let file = '';
    if (ev.dataTransfer.items) {
      // Use DataTransferItemList interface to access the file(s)
      file = [...ev.dataTransfer.items].find((item) => item.kind === 'file')?.getAsFile();
    } else {
      // Use DataTransfer interface to access the file(s)
      [file] = ev.dataTransfer.files;
    }
    setFiles([file]);
  };

  const onDragOver = (ev) => ev.preventDefault();

  const handleSubmit = async (event) => {
    event.stopPropagation();
    event.preventDefault();

    const data = new FormData();
    data.append('cv', files[0]);
    if (maxFileUploadSize < files[0].size) {
      dispatch(cvOversize(true));
      return;
    }

    if (!correctFileExt.includes(files[0].name.split('.').pop())) {
      setWrongFileType(true);
      return;
    }

    try {
      await dispatch(cvUpload(data));
      await dispatch(cvParse(data));
    } catch (err) {
      console.warn(err.message);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        {files.length ? (
          <FilesAdded
            files={files}
            setFiles={(d) => {
              setFiles(d);
              setWrongFileType(false);
              dispatch(cvOversize(false));
            }}
          />
        ) : (
          <button
            className={`${style.drop_zone} mt-5 text-center d-grid p-5`}
            onDrop={onDropHandler}
            onDragOver={onDragOver}
            type="button"
            data-testid="drop-zone"
          >
            <label htmlFor="file_picker" className={`${style.fp_label}`}>
              <img src={upload} className={`${style.img_upload} `} alt="file upload" />
              <div>
                <label htmlFor="file_picker" className={style.label_drop_zone}>
                  {t('profile.builder.start.drop')}
                </label>
                <br />
                <label htmlFor="file_picker" className={style.label_drop_zone_bold}>
                  <Trans i18nKey="profile.builder.start.browse">
                    or<span> browse</span>
                  </Trans>
                </label>
              </div>

              <input
                id="file_picker"
                type="file"
                accept=".pdf,.docx,.doc"
                onChange={(ev) => {
                  setFiles([...files, ev.target.files[0]]);
                }}
                style={{ display: 'none' }}
              />
            </label>
          </button>
        )}
        <div className="row pt-5 justify-content-center">
          <div className="col-8">
            {files.length !== 0 && !error && !wrongFileType ? (
              <input
                data-testid="files-added"
                type="submit"
                value={t('common.general.next')}
                className="button p-3 px-5 mt-4 text-capitalize"
              />
            ) : (
              <div className="">
                {wrongFileType || error ? (
                  <h5 data-testid="errorMsg" className={style.uploadSinglefileWarning}>
                    {error || validationMessage.CV_ALLOWED_FORMATS}
                  </h5>
                ) : (
                  <div className="">
                    {filesNumber > 1 ? (
                      <h5 className={style.uploadSinglefileWarning}>{validationMessage.CV_ONLY_ONE_FILE}</h5>
                    ) : (
                      <p>{t('profile.builder.start.continue')}</p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

export default DropOrChooseFile;
